/**
 * CampaignPrefetch – loads and caches the full planned-campaigns list.
 *
 * Call `CampaignPrefetch.prefetch()` once at application startup (fire-and-forget).
 * Subsequent consumers use `CampaignPrefetch.getCampaigns()` to read from the
 * cache without hitting the network.
 *
 * The cache is client-id–scoped so there is no data leakage when users switch
 * clients.  It expires after 1 hour (configurable via CAMPAIGNS_TTL_MS).
 */

import { AppCache } from '../../../shared/services/app-cache';
import { getCampaignData } from '../../../shared/services/campaign-api';

const CACHE_KEY_PREFIX = 'planned-campaigns';
const CAMPAIGNS_TTL_MS = 60 * 60 * 1000; // 1 hour

// Tracks the in-flight prefetch promise so concurrent callers share one fetch.
let prefetchPromise = null;

function buildCacheKey() {
  const clientId = sessionStorage.getItem('clientid') || 'default';
  return `${CACHE_KEY_PREFIX}-${clientId}`;
}

export const CampaignPrefetch = {
  /**
   * Fetch all planned campaigns and store them in AppCache.
   * Safe to call multiple times – deduplicates concurrent requests.
   * Intended to be called at application load (fire-and-forget).
   * @returns {Promise<void>}
   */
  async prefetch() {
    try {
      const key = buildCacheKey();

      // Already cached – nothing to do.
      const cached = await AppCache.get(key);
      if (cached) return;

      // Deduplicate concurrent prefetch calls.
      if (prefetchPromise) {
        await prefetchPromise;
        return;
      }

      prefetchPromise = getCampaignData(
        { 'source': 'planned', 'page-count': 2500 },
        null,
        { 'is-archive': false },
      ).then(async (response) => {
        const items = response?.data?.Items || [];
        await AppCache.set(key, items, CAMPAIGNS_TTL_MS);
      }).finally(() => {
        prefetchPromise = null;
      });

      await prefetchPromise;
    } catch {
      // Prefetch failure is non-critical; CopyPlacementsDialog falls back to a
      // direct API call when getCampaigns() returns null.
      prefetchPromise = null;
    }
  },

  /**
   * Return the cached campaigns list, or null if the cache is cold/expired.
   * @returns {Promise<Array|null>}
   */
  async getCampaigns() {
    return AppCache.get(buildCacheKey());
  },

  /**
   * Store a campaigns list in the cache (e.g., after a direct API call).
   * @param {Array} campaigns
   * @returns {Promise<void>}
   */
  async setCampaigns(campaigns) {
    return AppCache.set(buildCacheKey(), campaigns, CAMPAIGNS_TTL_MS);
  },

  /**
   * Invalidate the campaigns cache.
   * Call this after any operation that mutates the campaigns list.
   * @returns {Promise<void>}
   */
  async invalidate() {
    prefetchPromise = null;
    return AppCache.delete(buildCacheKey());
  },
};
